import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { OptimizedImage } from "../components/OptimizedImage";
import { LanguageSwitcher } from "../components/i18n/LanguageSwitcher";
import {
  Menu, X, Brain, ArrowRight, Sparkles, CheckCircle, CheckCircle2,
  FileText, Shield, Clock, BarChart3, Users,
  Upload, Settings, Download,
  TrendingUp, Award, Heart, Zap,
  Mail, Crown, Building2, Star, HelpCircle,
  BookOpen, Lightbulb, Target, AlertCircle, Calendar, Lock
} from "lucide-react";

// ==================== NAVBAR ====================
const Navbar = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation('landing');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: t('nav.features'), href: "#features" },
    { label: t('nav.howItWorks'), href: "#how-it-works" },
    { label: t('nav.benefits'), href: "#benefits" },
    { label: t('nav.pricing'), href: "#pricing" },
    { label: t('nav.faq'), href: "#faq" },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-soft" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16 lg:h-20">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group cursor-pointer">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-soft group-hover:shadow-card transition-shadow duration-300">
              <Brain className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground">Psymetrika</span>
          </button>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-muted-foreground hover:text-foreground font-medium transition-colors duration-200 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher variant="minimal" />
            <Button variant="ghost" onClick={() => navigate('/login')}>{t('nav.login')}</Button>
            <Button variant="gradient" onClick={() => navigate('/signup')}>{t('nav.startFree')}</Button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass rounded-2xl mb-4 overflow-hidden"
          >
            <div className="p-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="text-muted-foreground hover:text-foreground font-medium py-2 transition-colors cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
              <hr className="border-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('nav.language', 'Idioma')}</span>
                <LanguageSwitcher variant="buttons" showLabel={false} />
              </div>
              <Button variant="ghost" className="w-full" onClick={() => navigate('/login')}>{t('nav.login')}</Button>
              <Button variant="gradient" className="w-full" onClick={() => navigate('/signup')}>{t('nav.startFree')}</Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

// ==================== HERO ====================
const Hero = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation('landing');
  const features = [
    t('hero.features.tests'),
    t('hero.features.ai'),
    t('hero.features.reports'),
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/20">
      {/* Animated background patterns */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0 animate-pulse-slow" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(262.1 83.3% 57.8%) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Floating gradient orbs */}
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl animate-blob animation-delay-2000" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-indigo-400/10 to-purple-400/10 rounded-full blur-3xl animate-blob animation-delay-4000" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 lg:pt-32 pb-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="relative z-10 order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200/50 mb-6 shadow-sm"
            >
              <Sparkles className="w-4 h-4 text-purple-600 animate-pulse" />
              <span className="text-sm font-semibold text-purple-700">{t('hero.badge')}</span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.1] mb-4 sm:mb-6 tracking-tight text-center sm:text-left">
              {t('hero.titlePart1')}{" "}
              <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent animate-gradient">
                {t('hero.titleHighlight')}
              </span>{" "}
              {t('hero.titlePart2')}
            </h1>

            {/* Subtitulo corto para movil */}
            <p className="sm:hidden text-base text-slate-600 mb-6 leading-relaxed">
              {t('hero.subtitleMobile')}
            </p>

            {/* Subtitulo completo para desktop */}
            <p className="hidden sm:block text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
              {t('hero.subtitleDesktopPart1')} <span className="font-semibold text-slate-800">{t('hero.subtitleDesktopHighlight')}</span>{t('hero.subtitleDesktopPart2')}
            </p>

            <ul className="flex flex-col gap-3 sm:gap-4 mb-8 sm:mb-10">
              {features.map((feature, index) => (
                <motion.li
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3 group"
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <span className="text-sm sm:text-base text-slate-700 font-medium">{feature}</span>
                </motion.li>
              ))}
            </ul>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                variant="hero"
                size="xl"
                onClick={() => navigate('/signup')}
                className="group shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-300"
              >
                {t('hero.cta')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                size="xl"
                onClick={() => navigate('/login')}
                className="border-2 border-slate-300 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300"
              >
                {t('nav.login')}
              </Button>
            </motion.div>

            {/* Social proof - Solo en desktop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="hidden sm:flex mt-8 items-center gap-6"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 border-2 border-white shadow-md" />
                ))}
              </div>
              <div className="text-sm">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mt-1">
                  <span className="font-bold text-slate-900">{t('hero.socialProof')}</span> {t('hero.socialProofSuffix')}
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="hidden lg:block relative order-1 lg:order-2"
          >
            <div className="relative">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl blur-3xl opacity-20 animate-pulse-slow" />

              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50 backdrop-blur">
                <OptimizedImage
                  src="/psymetrika-brain-hero.jpg"
                  alt="Psymetrika - Evaluaciones de salud mental con IA"
                  className="w-full h-auto object-cover"
                  sizes="(max-width: 1024px) 100vw, 800px"
                  priority={true}
                />

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent" />
              </div>

              {/* Floating stats cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-4 border border-purple-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">42</p>
                    <p className="text-xs text-slate-600">{t('hero.statsTests')}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-2xl p-4 border border-blue-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">3-5s</p>
                    <p className="text-xs text-slate-600">{t('hero.statsAI')}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== FEATURES ====================
const getFeaturesData = (t: (key: string) => string) => [
  {
    icon: Brain,
    title: t('features.list.ai.title'),
    description: t('features.list.ai.description'),
  },
  {
    icon: FileText,
    title: t('features.list.tests.title'),
    description: t('features.list.tests.description'),
  },
  {
    icon: Clock,
    title: t('features.list.time.title'),
    description: t('features.list.time.description'),
  },
  {
    icon: Shield,
    title: t('features.list.security.title'),
    description: t('features.list.security.description'),
  },
  {
    icon: BarChart3,
    title: t('features.list.analysis.title'),
    description: t('features.list.analysis.description'),
  },
  {
    icon: Users,
    title: t('features.list.patients.title'),
    description: t('features.list.patients.description'),
  },
];

const Features = () => {
  const { t } = useTranslation('landing');
  const featuresData = getFeaturesData(t);
  const featureColors = [
    { gradient: "from-purple-500 to-indigo-600", bg: "bg-purple-50", border: "border-purple-200" },
    { gradient: "from-blue-500 to-cyan-600", bg: "bg-blue-50", border: "border-blue-200" },
    { gradient: "from-green-500 to-emerald-600", bg: "bg-green-50", border: "border-green-200" },
    { gradient: "from-orange-500 to-red-600", bg: "bg-orange-50", border: "border-orange-200" },
    { gradient: "from-pink-500 to-rose-600", bg: "bg-pink-50", border: "border-pink-200" },
    { gradient: "from-violet-500 to-purple-600", bg: "bg-violet-50", border: "border-violet-200" },
  ];

  return (
    <section id="features" className="py-8 lg:py-12 bg-gradient-to-b from-white via-purple-50/30 to-white relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0 animate-pulse-slow" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(262.1 83.3% 57.8%) 1px, transparent 0)`,
          backgroundSize: '48px 48px'
        }} />
      </div>

      {/* Floating orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-purple-300/20 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-blue-300/20 rounded-full blur-3xl animate-blob animation-delay-2000" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 font-bold text-sm uppercase tracking-wider">{t('features.badge')}</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight">
            {t('features.titlePart1')}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600">
              {t('features.titleHighlight')}
            </span>
          </h2>
          <p className="mt-6 text-xl text-slate-600 leading-relaxed">
            {t('features.subtitle')}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {featuresData.map((feature, index) => {
            const colors = featureColors[index % featureColors.length];
            const shouldCenterPair = featuresData.length % 3 === 2 && index === featuresData.length - 2;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative lg:col-span-2 ${shouldCenterPair ? "lg:col-start-2" : ""}`}
              >
                {/* Hover glow effect */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${colors.gradient} rounded-2xl opacity-0 group-hover:opacity-30 blur transition duration-500`} />

                {/* Card */}
                <div className="relative bg-white rounded-2xl p-5 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 group-hover:border-transparent h-full">
                  {/* PRO Badge */}
                  {feature.badge && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full shadow-lg">
                        <Crown className="w-3 h-3" />
                        {feature.badge}
                      </span>
                    </div>
                  )}

                  <div className={`w-12 h-12 lg:w-16 lg:h-16 rounded-xl lg:rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-4 lg:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <feature.icon className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                  </div>

                  <h3 className="text-lg lg:text-2xl font-bold text-slate-900 mb-3 lg:mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-sm lg:text-base">
                    {feature.description}
                  </p>

                  {/* Decorative elements */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ==================== EVOLUTIVOS SECTION ====================
const EvolutivosSection = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
              {t('evolutivos.badge')}
            </span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('evolutivos.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('evolutivos.subtitle')}
          </p>
        </div>

        {/* Two-column grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-stretch mb-12">
          {/* Left Column: What are evolutivos */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-full"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl h-full">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-purple-600" />
                {t('evolutivos.whatAre.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('evolutivos.whatAre.description') }} />

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('evolutivos.whatAre.structured.title')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('evolutivos.whatAre.structured.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('evolutivos.whatAre.longTerm.title')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('evolutivos.whatAre.longTerm.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {t('evolutivos.whatAre.ocr.title')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('evolutivos.whatAre.ocr.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: AI Analysis */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-full"
          >
            <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-8 shadow-xl text-white h-full">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                {t('evolutivos.aiAnalysis.title')}
              </h3>
              <p className="mb-6 opacity-90 leading-relaxed">
                {t('evolutivos.aiAnalysis.description')}
              </p>

              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    {t('evolutivos.aiAnalysis.summary.title')}
                  </h4>
                  <p className="text-sm opacity-90">
                    {t('evolutivos.aiAnalysis.summary.description')}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    {t('evolutivos.aiAnalysis.trends.title')}
                  </h4>
                  <p className="text-sm opacity-90">
                    {t('evolutivos.aiAnalysis.trends.description')}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    {t('evolutivos.aiAnalysis.classification.title')}
                  </h4>
                  <p className="text-sm opacity-90">
                    {t('evolutivos.aiAnalysis.classification.description')}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    {t('evolutivos.aiAnalysis.recommendations.title')}
                  </h4>
                  <p className="text-sm opacity-90">
                    {t('evolutivos.aiAnalysis.recommendations.description')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA Box */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border-2 border-purple-200 dark:border-purple-700">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('evolutivos.cta.proIncluded')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('evolutivos.cta.proAnalyses')}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {t('evolutivos.cta.proPdfs')}
                </p>
              </div>
              <div className="sm:text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('evolutivos.cta.freePlan')}
                </p>
                <p className="text-lg font-semibold text-gray-500 dark:text-gray-500">
                  {t('evolutivos.cta.freeUnlimited')}<br />
                  <span className="text-sm">{t('evolutivos.cta.freeNoAi')}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {t('evolutivos.cta.freePdfs')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
            >
              {t('evolutivos.cta.button')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Visual Example of Evolutivo Types */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl"
        >
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            {t('evolutivos.types.title')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t('evolutivos.types.single.title')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('evolutivos.types.single.description')}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t('evolutivos.types.short.title')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('evolutivos.types.short.description')}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t('evolutivos.types.medium.title')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('evolutivos.types.medium.description')}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t('evolutivos.types.long.title')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('evolutivos.types.long.description')}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t('evolutivos.types.crisis.title')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('evolutivos.types.crisis.description')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== HOW IT WORKS ====================
const getSteps = (t: (key: string) => string) => [
  {
    icon: Upload,
    step: "01",
    title: t('howItWorks.steps.create.title'),
    description: t('howItWorks.steps.create.description'),
  },
  {
    icon: Settings,
    step: "02",
    title: t('howItWorks.steps.select.title'),
    description: t('howItWorks.steps.select.description'),
  },
  {
    icon: Sparkles,
    step: "03",
    title: t('howItWorks.steps.analyze.title'),
    description: t('howItWorks.steps.analyze.description'),
  },
  {
    icon: Download,
    step: "04",
    title: t('howItWorks.steps.export.title'),
    description: t('howItWorks.steps.export.description'),
  },
];

const HowItWorks = () => {
  const { t } = useTranslation('landing');
  const steps = getSteps(t);
  return (
    <section id="how-it-works" className="py-8 lg:py-12 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, hsl(262.1 83.3% 57.8%) 1px, transparent 1px), linear-gradient(to bottom, hsl(262.1 83.3% 57.8%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 font-bold text-sm uppercase tracking-wider">{t('howItWorks.badge')}</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight">
            {t('howItWorks.titlePart1')}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600">
              {t('howItWorks.titleHighlight')}
            </span>
          </h2>
          <p className="mt-6 text-xl text-slate-600 leading-relaxed">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        <div className="relative max-w-6xl mx-auto">
          {/* Connection line between steps */}
          <div className="hidden lg:block absolute top-[72px] left-[15%] right-[15%] h-1 bg-gradient-to-r from-purple-200 via-blue-200 to-cyan-200 rounded-full" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative text-center group"
              >
                {/* Step number badge */}
                <div className="relative inline-flex flex-col items-center mb-4 lg:mb-6">
                  <div className="relative">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl blur-xl opacity-0 group-hover:opacity-40 transition duration-500" />

                    {/* Icon container */}
                    <div className="relative w-16 h-16 lg:w-24 lg:h-24 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <step.icon className="w-7 h-7 lg:w-10 lg:h-10 text-white" />
                    </div>

                    {/* Step number */}
                    <span className="absolute -top-2 -right-2 lg:-top-3 lg:-right-3 w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white border-2 lg:border-4 border-slate-100 text-slate-900 text-xs lg:text-sm font-black flex items-center justify-center shadow-lg z-20">
                      {step.step}
                    </span>
                  </div>
                </div>

                <h3 className="text-lg lg:text-2xl font-bold text-slate-900 mb-2 lg:mb-3 group-hover:text-purple-600 transition-colors">
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm lg:text-base">
                  {step.description}
                </p>

                {/* Arrow connector (desktop only) */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-[72px] -right-[10%] z-10">
                    <ArrowRight className="w-6 h-6 text-purple-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA at bottom of How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <Button
            variant="hero"
            size="xl"
            onClick={() => window.location.href = '/signup'}
            className="group shadow-2xl shadow-purple-500/30"
          >
            {t('howItWorks.cta')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="mt-4 text-sm text-slate-600">
            {t('howItWorks.ctaSubtext')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== BENEFITS ====================
const getBenefitsData = (t: (key: string) => string) => [
  {
    icon: TrendingUp,
    stat: "80%",
    label: t('benefits.stats.time.label'),
    description: t('benefits.stats.time.description'),
  },
  {
    icon: Award,
    stat: "100%",
    label: t('benefits.stats.compliance.label'),
    description: t('benefits.stats.compliance.description'),
  },
  {
    icon: Heart,
    stat: "95%",
    label: t('benefits.stats.satisfaction.label'),
    description: t('benefits.stats.satisfaction.description'),
  },
  {
    icon: Zap,
    stat: "40+",
    label: t('benefits.stats.tests.label'),
    description: t('benefits.stats.tests.description'),
  },
];

const Benefits = () => {
  const { t } = useTranslation('landing');
  const benefitsData = getBenefitsData(t);
  const gradients = [
    "from-purple-500 to-indigo-600",
    "from-blue-500 to-cyan-600",
    "from-green-500 to-emerald-600",
    "from-orange-500 to-amber-600"
  ];

  return (
    <section id="benefits" className="py-8 lg:py-12 bg-gradient-to-br from-purple-50 via-white to-blue-50 relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-300/30 to-blue-300/30 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-300/30 to-cyan-300/30 rounded-full blur-3xl animate-blob animation-delay-2000" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto">
          {/* Left Column - Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 font-bold text-sm uppercase tracking-wider">{t('benefits.badge')}</span>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight">
              {t('benefits.titlePart1')}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600">
                {t('benefits.titleHighlight')}
              </span>
            </h2>
            <p className="mt-6 text-xl text-slate-600 leading-relaxed">
              {t('benefits.description')}
            </p>

            <div className="mt-12 space-y-5">
              {[
                { text: t('benefits.checkmarks.standardized'), icon: CheckCircle },
                { text: t('benefits.checkmarks.interpretation'), icon: Sparkles },
                { text: t('benefits.checkmarks.reports'), icon: FileText },
                { text: t('benefits.checkmarks.history'), icon: TrendingUp },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-700 text-lg font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Stats Grid */}
          <div className="grid grid-cols-2 gap-5 lg:gap-6">
            {benefitsData.map((benefit, index) => {
              const gradient = gradients[index % gradients.length];
              return (
                <motion.div
                  key={benefit.label}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  {/* Glow on hover */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-3xl opacity-0 group-hover:opacity-40 blur transition duration-500`} />

                  {/* Card */}
                  <div className="relative bg-white rounded-3xl p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-200 group-hover:border-transparent">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                      <benefit.icon className="w-7 h-7 text-white" />
                    </div>

                    <div className={`text-4xl lg:text-5xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-r ${gradient}`}>
                      {benefit.stat}
                    </div>
                    <div className="font-bold text-slate-900 text-lg mb-1">{benefit.label}</div>
                    <div className="text-sm text-slate-600">{benefit.description}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== PRICING ====================
const Pricing = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation('landing');
  const plans = [
    {
      name: t('pricing.plans.free.name'),
      icon: Zap,
      price: t('pricing.plans.free.price'),
      period: t('pricing.plans.free.period'),
      description: t('pricing.plans.free.description'),
      features: t('pricing.plans.free.features', { returnObjects: true }) as string[],
      cta: t('pricing.plans.free.cta'),
      highlighted: false,
    },
    {
      name: t('pricing.plans.pro.name'),
      icon: Crown,
      price: t('pricing.plans.pro.price'),
      period: t('pricing.plans.pro.period'),
      description: t('pricing.plans.pro.description'),
      badge: t('pricing.plans.pro.badge'),
      features: t('pricing.plans.pro.features', { returnObjects: true }) as string[],
      cta: t('pricing.plans.pro.cta'),
      highlighted: true,
    },
    {
      name: t('pricing.plans.enterprise.name'),
      icon: Building2,
      price: t('pricing.plans.enterprise.price'),
      period: '',
      description: t('pricing.plans.enterprise.description'),
      features: t('pricing.plans.enterprise.features', { returnObjects: true }) as string[],
      cta: t('pricing.plans.enterprise.cta'),
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" className="py-4 lg:py-6 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('pricing.badge')}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">
            {t('pricing.title')}
          </h2>
          <p className="mt-6 text-lg text-muted-foreground">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className={`relative bg-card rounded-2xl shadow-soft hover:shadow-card transition-all duration-300 border border-border/50 ${
                plan.highlighted ? 'ring-2 ring-primary transform md:scale-105' : ''
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <span className="bg-gradient-primary text-primary-foreground px-4 py-1.5 rounded-full text-sm font-bold shadow-soft">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4">
                    <plan.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">{plan.name}</h3>
                  <p className="text-muted-foreground text-sm">{plan.description}</p>
                </div>

                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-extrabold text-foreground">{plan.price}</span>
                    {plan.period && (
                      <span className="text-muted-foreground ml-2 text-lg">{plan.period}</span>
                    )}
                  </div>
                </div>

                <Button
                  variant={plan.highlighted ? "gradient" : "default"}
                  className="w-full mb-8"
                  size="lg"
                  onClick={() => {
                    if (plan.name === 'Free') navigate('/signup');
                    else if (plan.name === 'Enterprise') navigate('/contact');
                    else navigate('/pricing');
                  }}
                >
                  {plan.cta}
                </Button>

                <div className="space-y-4">
                  {plan.features.map((feature, featureIdx) => (
                    <div key={featureIdx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-6 py-3 rounded-full border border-green-200">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold text-sm">{t('pricing.guarantee')}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== FAQ ====================
const getFaqData = (t: (key: string) => string) => [
  {
    question: t('faq.questions.technical.question'),
    answer: t('faq.questions.technical.answer')
  },
  {
    question: t('faq.questions.validated.question'),
    answer: t('faq.questions.validated.answer')
  },
  {
    question: t('faq.questions.aiAnalysis.question'),
    answer: t('faq.questions.aiAnalysis.answer')
  },
  {
    question: t('faq.questions.security.question'),
    answer: t('faq.questions.security.answer')
  },
  {
    question: t('faq.questions.cancel.question'),
    answer: t('faq.questions.cancel.answer')
  },
  {
    question: t('faq.questions.freePlan.question'),
    answer: t('faq.questions.freePlan.answer')
  },
  {
    question: t('faq.questions.devices.question'),
    answer: t('faq.questions.devices.answer')
  },
  {
    question: t('faq.questions.support.question'),
    answer: t('faq.questions.support.answer')
  },
];

const FAQ = () => {
  const { t } = useTranslation('landing');
  const faqData = getFaqData(t);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-4 lg:py-6 bg-background relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('faq.badge')}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">
            {t('faq.titlePart1')}{" "}
            <span className="text-gradient">{t('faq.titleHighlight')}</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="bg-card rounded-xl border border-border/50 overflow-hidden hover:shadow-soft transition-shadow duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left group"
              >
                <div className="flex items-start gap-4 flex-1">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {faq.question}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <ArrowRight className="w-5 h-5 text-muted-foreground rotate-90" />
                </motion.div>
              </button>

              <motion.div
                initial={false}
                animate={{
                  height: openIndex === index ? "auto" : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-5 pl-15">
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground mb-4">
            {t('faq.moreQuestions')}
          </p>
          <Button variant="outline" size="lg" onClick={() => window.location.href = 'mailto:soporte@psymetrika.com'}>
            <Mail className="w-5 h-5" />
            {t('faq.contactSupport')}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== CTA ====================
const CTA = ({ navigate }: { navigate: (path: string) => void }) => {
  const { t } = useTranslation('landing');
  return (
    <section className="py-4 lg:py-6 bg-gradient-hero relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('cta.titlePart1')}{" "}
            <span className="text-gradient">{t('cta.titleHighlight')}</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button variant="hero" size="xl" onClick={() => navigate('/signup')}>
              {t('cta.button')}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button variant="glass" size="xl" onClick={() => navigate('/contact')}>
              <Mail className="w-5 h-5" />
              {t('cta.contactSales')}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('cta.disclaimer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const getFooterLinks = (t: (key: string) => string) => ({
  producto: [
    { label: t('footer.links.features'), href: "#features" },
    { label: t('footer.links.pricing'), href: "#pricing" },
    { label: t('footer.links.tests'), href: "/tests" },
  ],
  recursos: [
    { label: t('footer.links.docs'), href: "/docs" },
    { label: t('footer.links.guides'), href: "/guides" },
    { label: t('footer.links.blog'), href: "#blog" },
  ],
  empresa: [
    { label: t('footer.links.about'), href: "/about" },
    { label: t('footer.links.contact'), href: "/contact" },
  ],
  legal: [
    { label: t('footer.links.privacy'), href: "/privacy" },
    { label: t('footer.links.terms'), href: "/terms" },
  ],
});

const Footer = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const footerLinks = getFooterLinks(t);

  const handleLinkClick = (href: string) => (e: React.MouseEvent) => {
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 lg:gap-12">
          <div className="col-span-2">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-4 cursor-pointer">
              <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
                <Brain className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-foreground">Psymetrika</span>
            </button>
            <p className="text-muted-foreground mb-6 max-w-xs">
              {t('footer.description')}
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">{t('footer.sections.product')}</h4>
            <ul className="space-y-3">
              {footerLinks.producto.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={handleLinkClick(link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">{t('footer.sections.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.recursos.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={handleLinkClick(link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">{t('footer.sections.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={handleLinkClick(link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">{t('footer.sections.legal')}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={handleLinkClick(link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-sm text-muted-foreground">
              {t('footer.madeWith')}
            </p>
            <span className="hidden sm:block text-muted-foreground">·</span>
            <p className="text-sm text-muted-foreground">
              {t('footer.createdBy')}{" "}
              <a
                href="https://arpweb.vercel.app"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                ARP Web Design
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== MAIN PAGE ====================
export default function LandingPage() {
  const navigate = useNavigate();

  // Ensure dashboard-active class is removed when landing page mounts
  // This prevents scroll blocking issues when navigating from dashboard
  useEffect(() => {
    // Aggressively remove the class and reset body/html styles
    document.body.classList.remove('dashboard-active');
    document.documentElement.classList.remove('dashboard-active');

    // Force reset scroll behavior on body and html for mobile
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.height = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';

    // Monitor and remove the class if it gets added again (production build issue)
    const observer = new MutationObserver(() => {
      if (document.body.classList.contains('dashboard-active')) {
        document.body.classList.remove('dashboard-active');
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.height = '';
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Cleanup
    return () => {
      observer.disconnect();
      document.body.classList.remove('dashboard-active');
    };
  }, []);

  return (
    <>
      <SEO
        title="Evaluación Psicométrica con IA | Psymetrika"
        description="Plataforma de tests psicométricos con IA para psicólogos y psiquiatras. +40 tests validados, análisis en 3s y reportes PDF. ¡Prueba gratis!"
        keywords="tests psicométricos online, evaluación psicológica con IA, plataforma psicometría profesional, PHQ-9 GAD-7 online, software psicólogos, análisis clínico inteligencia artificial, gestión pacientes psicología, reportes psicológicos automáticos"
        canonical="/"
        ogImage="https://psymetrika.com/og-image.webp"
      />
      <div className="min-h-screen bg-background">
        <Navbar navigate={navigate} />
        <main>
        <Hero navigate={navigate} />
        <Features />
        <HowItWorks />
        <Benefits />
        <Pricing navigate={navigate} />
        <FAQ />
        <CTA navigate={navigate} />
        </main>
        <Footer />
      </div>
    </>
  );
}
